import React, { useState } from 'react';
import {
  Typography,
  Popconfirm,
  notification,
  Space,
  Form,
  Input,
  Modal,
  Select,
  Tabs,
  Alert,
} from 'antd';
import { useParams, Link } from 'react-router-dom';

import BreadcrumbContainer from '../../../containers/Breadcrumb';
import INTERNAL_LINKS from '../../../enums/InternalLinks';
import MESSAGES from '../../../enums/Messages';
import CustomButton from '../../../components/CustomButton';
import AjaxTable from '../../../components/AjaxTable';
import CustomSelector from '../../../components/CustomSelector';
import { CustomTooltip } from '../../../components/CustomTooltip';

import { getAll as getCountries } from '../../../api/module/countries';
import { getByCountry as getStates } from '../../../api/module/states';
import {
  getByState as getCitiesByState,
  getByCountry as getCitiesByCountry,
} from '../../../api/module/cities';
import {
  assignResumes,
  ajaxTable as placesTable,
  post,
  put,
  remove,
  resumeTable,
  unassignResume,
} from '../../../api/module/term_places';

const { Title, Paragraph } = Typography;
const { TextArea } = Input;

const TermPlace = () => {
  const { term_id, project_id, contract_id } = useParams();

  const [initialValues, setInitialValues] = useState();
  const [placeSelection, setPlaceSelection] = useState();
  const [resumeSelection, setResumeSelection] = useState([]);
  const [reload, setReload] = useState({ place: false, resume: false });
  const [deleteID, setDeleteID] = useState();
  const [loadingForm, setLoadingForm] = useState(false);
  const [loadingAssign, setLoadingAssign] = useState(false);
  const [unassignID, setUnassignID] = useState();

  const breadcrumbItems = [
    {
      text: 'Términos de referencia',
      to: `${INTERNAL_LINKS.TDR_HOME}`,
    },
    {
      text: 'Formulario de Términos de referencia',
      to: `${INTERNAL_LINKS.TERM}/${term_id}/${project_id}/${contract_id}`,
    },
    {
      text: 'Lugar de ejecución',
    },
  ];

  const onFinish = async (values) => {
    setLoadingForm(true);
    try {
      const data = {
        ...values,
        termino: term_id,
      };
      if (initialValues?.id) {
        await put(initialValues.id, data);
        setReload({ place: {}, resume: {} });
        if (initialValues.id === placeSelection?.id) setPlaceSelection();
      } else {
        await post(data);
        setReload((s) => ({ place: {}, resume: s.resume }));
      }
      setInitialValues();
    } catch (error) {
      notification.error({
        message: MESSAGES.ERROR_MSG,
        description: MESSAGES.DESC_ERROR_POST_MSG,
        duration: 2,
      });
    } finally {
      setLoadingForm(false);
    }
  };

  const handleDelete = async (id) => {
    setDeleteID(id);
    try {
      await remove(id);
      setReload({ place: {}, resume: {} });
      if (id === placeSelection?.id) setPlaceSelection();
    } catch (error) {
      notification.error({
        message: MESSAGES.ERROR_MSG,
        description: MESSAGES.DESC_ERROR_DELETE_MSG,
        duration: 2,
      });
    } finally {
      setDeleteID(undefined);
    }
  };

  const placeColumns = [
    {
      title: 'País de ejecución',
      dataIndex: 'pais_texto',
    },
    {
      title: 'Departamento de ejecución',
      dataIndex: 'estado_texto',
    },
    {
      title: 'Ciudad de ejecución',
      dataIndex: 'ciudad_texto',
    },
    {
      title: 'Personas asignadas',
      dataIndex: 'num_personas',
    },
    {
      title: 'Acciones',
      key: 'acciones',
      render: (_, record) => {
        return (
          <Space direction={'vertical'} style={{ width: '100%' }}>
            <CustomButton
              block
              htmlType={'button'}
              text="Editar"
              disabled={
                record.id === deleteID || record.id === initialValues?.id
              }
              onClick={() =>
                setInitialValues({
                  ...record,
                  pais_varios:
                    record.pais === 9999999 ? record.pais_texto : undefined,
                  estado_varios:
                    record.estado === 9999999 ? record.estado_texto : undefined,
                  ciudad_varios:
                    record.ciudad === 9999999 ? record.ciudad_texto : undefined,
                  open: true,
                })
              }
            />
            <CustomButton
              block
              type={'default'}
              htmlType={'button'}
              loading={record.id === deleteID}
              danger
              text={
                <Popconfirm
                  title="¿Ésta seguro de eliminar este lugar? Si hay personas asignadas se desasignarán."
                  onConfirm={() => handleDelete(record.id)}
                >
                  Eliminar
                </Popconfirm>
              }
            />
          </Space>
        );
      },
    },
  ];

  const resumeColumns = [
    {
      title: 'Nombres y apellidos',
      dataIndex: 'nombre',
    },
    {
      title: 'Tipo de documento',
      dataIndex: 'tipo_identificacion',
    },
    {
      title: 'Nro. Documento',
      dataIndex: 'identificacion',
    },
    {
      title: 'País residencia',
      dataIndex: 'pais_residencia',
    },
    {
      title: 'Departamento residencia',
      dataIndex: 'estado_residencia',
    },
    {
      title: 'Ciudad residencia',
      dataIndex: 'ciudad_residencia',
    },
    {
      title: 'País ejecución',
      dataIndex: 'pais_asignado',
    },
    {
      title: 'Departamento ejecución',
      dataIndex: 'estado_asignado',
    },
    {
      title: 'Ciudad ejecución',
      dataIndex: 'ciudad_asignado',
    },
  ];

  const placeRowSelection = {
    type: 'radio',
    selectedRowKeys: [placeSelection?.id],
    preserveSelectedRowKeys: true,
    onChange: (_selectedRowKeys, selectedRows) => {
      if (selectedRows.length > 0) setPlaceSelection(selectedRows[0]);
      else setPlaceSelection();
    },
  };

  const resumeRowSelection = {
    fixed: true,
    hideSelectAll: false,
    preserveSelectedRowKeys: true,
    selectedRowKeys: resumeSelection,
    onChange: (_selectedRowKeys, selectedRows, { type: ct }) => {
      function doSelect(sr) {
        if (sr.length > 0) {
          setResumeSelection(sr.filter((v) => !v.lugar).map((v) => v.id));
        } else {
          setResumeSelection([]);
        }
      }

      if (ct === 'all') {
        const act_prefix =
          selectedRows.length > resumeSelection.length
            ? 'selecciona'
            : 'deselecciona';
        Modal.info({
          title: 'Importante',
          content: `Esta acción ${act_prefix} únicamente los registros visualizados en la tabla. Use la paginación si desea ${act_prefix}r más registros.`,
          onOk: () => doSelect(selectedRows),
        });
        return;
      }
      doSelect(selectedRows);
    },
    renderCell: (_checked, record, _index, originNode) => {
      if (record.lugar) return null;
      return originNode;
    },
  };

  const handleAssign = async () => {
    if (!placeSelection || resumeSelection.length < 1) return;
    setLoadingAssign(true);
    try {
      await assignResumes(placeSelection.id, {
        resumes: resumeSelection,
      });
      setReload({ place: {}, resume: {} });
      setPlaceSelection();
      setResumeSelection([]);
    } catch (error) {
      notification.error({
        message: MESSAGES.ERROR_MSG,
        description: MESSAGES.DESC_ERROR_POST_MSG,
        duration: 2,
      });
    } finally {
      setLoadingAssign(false);
    }
  };

  const handleUnassign = async (id) => {
    setUnassignID(id);
    try {
      await unassignResume(id);
      setReload({ place: {}, resume: {} });
    } catch (error) {
      notification.error({
        message: MESSAGES.ERROR_MSG,
        description: MESSAGES.DESC_ERROR_DELETE_MSG,
        duration: 2,
      });
    } finally {
      setUnassignID();
    }
  };

  const resumeExpandedColumns = [
    {
      title: 'Nombres y apellidos',
      dataIndex: 'nombre',
    },
    {
      title: 'Tipo de documento',
      dataIndex: 'tipo_identificacion',
    },
    {
      title: 'Nro. Documento',
      dataIndex: 'identificacion',
    },
    {
      title: 'Departamento residencia',
      dataIndex: 'estado_residencia',
    },
    {
      title: 'Ciudad residencia',
      dataIndex: 'ciudad_residencia',
    },
    {
      title: 'Acciones',
      key: 'acciones',
      render: (_, record) => {
        return (
          <CustomButton
            block
            type="default"
            htmlType="button"
            loading={record.id === unassignID}
            text="Desasignar"
            onClick={() => handleUnassign(record.id)}
          />
        );
      },
    },
  ];

  const expandedRowRender = (record) => {
    return (
      <AjaxTable
        columns={resumeExpandedColumns}
        tableTitle={'Listado de contratistas asignados'}
        endpoint={resumeTable}
        reloadSource={reload.resume}
        scroll={{ x: false }}
        query={`${term_id}&filter[lugar]=${record.id}`}
        searchInput
      />
    );
  };

  return (
    <>
      <BreadcrumbContainer items={breadcrumbItems} />
      <Title level={2}>Asignación Lugar de ejecución</Title>
      <Alert
        message="Nota"
        description={
          <>
            Recuerde seleccionar el(los) contratista(s) en el módulo E. PERSONA
            A CONTRATAR / DONATARIO para poder asignarle(s) lugar de ejecución
            <ol>
              <li>Agregue el(los) lugar(es) de ejecución</li>
              <li>
                Para asignar contratistas a los lugares de ejecución creados:{' '}
                <ul>
                  <li>Seleccione el lugar de ejecución</li>
                  <li>Seleccione el(los) contratista(s)</li>
                  <li>De clic en el botón `Asignar lugar de ejecución`</li>
                </ul>
              </li>
            </ol>
          </>
        }
        type="info"
        showIcon
      />
      <br />
      <CustomButton
        htmlType="button"
        text="Agregar lugar de ejecución"
        onClick={() => setInitialValues({ open: true })}
      />
      <Modal
        open={initialValues?.open}
        destroyOnClose
        footer={null}
        closable={false}
        title={
          <>
            Lugar de ejecución
            <CustomTooltip
              placement="top"
              title="Seleccione el País, Departamento (aplica para Colombia) y ciudad en que se desarrollarán las actividades  del contrato o en el que se hace la donación"
            />
          </>
        }
      >
        <Form
          onFinish={onFinish}
          initialValues={initialValues}
          layout="vertical"
        >
          <Form.Item
            name="pais"
            label="País"
            rules={[{ required: true }]}
            valuePropName="initialValue"
          >
            <CustomSelector
              endpointFetch={getCountries}
              valueName="id"
              labelName="nombre"
            />
          </Form.Item>
          <Form.Item noStyle dependencies={['pais']}>
            {({ getFieldValue, setFieldsValue }) => {
              const p = getFieldValue('pais');
              if (p === 9999999)
                return (
                  <Form.Item
                    label="Países varios"
                    name="pais_varios"
                    rules={[{ required: true }]}
                  >
                    <TextArea placeholder="Digite los países" />
                  </Form.Item>
                );
              else setFieldsValue({ pais_varios: undefined });
              return null;
            }}
          </Form.Item>
          <Form.Item noStyle dependencies={['pais']}>
            {({ getFieldValue, setFieldsValue }) => {
              const p = getFieldValue('pais');
              if (p !== initialValues?.pais)
                setFieldsValue({ estado: undefined, estado_varios: undefined });
              if (p === 45)
                return (
                  <Form.Item
                    name="estado"
                    label="Departamento"
                    valuePropName="initialValue"
                  >
                    <CustomSelector
                      endpointFetch={async () => {
                        const res = await getStates(p);
                        res.data.results.push({
                          id: 9999999,
                          nombre: 'Varios',
                        });
                        return res;
                      }}
                      valueName="id"
                      labelName="nombre"
                      selectorKey={p}
                      allowClear
                    />
                  </Form.Item>
                );
              return (
                <Form.Item name="estado" label="Departamento">
                  <Select
                    options={[
                      {
                        value: 9999999,
                        label: 'Varios',
                      },
                    ]}
                    allowClear
                  />
                </Form.Item>
              );
            }}
          </Form.Item>
          <Form.Item noStyle dependencies={['estado']}>
            {({ getFieldValue, setFieldsValue }) => {
              const p = getFieldValue('estado');
              if (p === 9999999)
                return (
                  <Form.Item name="estado_varios">
                    <TextArea
                      placeholder="Digite los departamentos"
                      allowClear
                    />
                  </Form.Item>
                );
              else setFieldsValue({ estado_varios: undefined });
              return null;
            }}
          </Form.Item>
          <Form.Item noStyle dependencies={['pais', 'estado']}>
            {({ getFieldValue, setFieldsValue }) => {
              const p = getFieldValue('pais');
              const d = getFieldValue('estado');
              if (p !== initialValues?.pais || d !== initialValues?.estado)
                setFieldsValue({ ciudad: undefined, ciudad_varios: undefined });
              if (!p || p === 9999999 || d === 9999999) {
                return (
                  <Form.Item name="ciudad" label="Ciudad">
                    <Select
                      options={[
                        {
                          value: 9999999,
                          label: 'Varios',
                        },
                      ]}
                      allowClear
                    />
                  </Form.Item>
                );
              } else {
                return (
                  <Form.Item
                    name="ciudad"
                    label="Ciudad"
                    valuePropName="initialValue"
                  >
                    <CustomSelector
                      endpointFetch={async () => {
                        let res;
                        if (!d) {
                          res = await getCitiesByCountry(p);
                        } else {
                          res = await getCitiesByState(d);
                        }
                        res.data.results.push({
                          id: 9999999,
                          nombre: 'Varios',
                        });
                        return res;
                      }}
                      valueName="id"
                      labelName="nombre"
                      selectorKey={`${p}_${d}`}
                      allowClear
                    />
                  </Form.Item>
                );
              }
            }}
          </Form.Item>
          <Form.Item noStyle dependencies={['ciudad']}>
            {({ getFieldValue, setFieldsValue }) => {
              const p = getFieldValue('ciudad');
              if (p === 9999999)
                return (
                  <Form.Item name="ciudad_varios">
                    <TextArea placeholder="Digite las ciudades" allowClear />
                  </Form.Item>
                );
              else setFieldsValue({ ciudad_varios: undefined });
              return null;
            }}
          </Form.Item>
          <Space
            direction="horizontal"
            style={{ justifyContent: 'space-around', width: '100%' }}
          >
            <CustomButton
              htmlType="button"
              text="Cancelar"
              type="default"
              disabled={loadingForm}
              onClick={() => setInitialValues()}
            />
            <CustomButton
              htmlType="submit"
              type="primary"
              text="Guardar"
              loading={loadingForm}
            />
          </Space>
        </Form>
      </Modal>
      <AjaxTable
        rowSelection={placeRowSelection}
        columns={placeColumns}
        tableTitle={'Listado de lugares de ejecución'}
        endpoint={placesTable}
        expandedRowRender={{ expandedRowRender }}
        reloadSource={reload.place}
        searchInput
        scroll={{ x: false }}
        query={term_id}
        showSizeChanger
      />

      <Tabs
        type="card"
        destroyInactiveTabPane={true}
        // style={{ marginTop: 15 }}
        defaultActiveKey="s"
      >
        <Tabs.TabPane tab="Mostrar sin asignar" key="s">
          <AjaxTable
            rowSelection={resumeRowSelection}
            columns={resumeColumns}
            tableTitle={'Listado de contratistas para asignar'}
            endpoint={resumeTable}
            reloadSource={reload.resume}
            query={`${term_id}&filter[lugar__isnull]=true`}
            searchInput
            showSizeChanger
            pageSize={50}
          />
        </Tabs.TabPane>
        <Tabs.TabPane tab="Mostrar todos" key="t">
          <AjaxTable
            rowSelection={resumeRowSelection}
            columns={resumeColumns}
            tableTitle={'Listado de contratistas'}
            endpoint={resumeTable}
            reloadSource={reload.resume}
            query={term_id}
            searchInput
            showSizeChanger
            pageSize={50}
          />
        </Tabs.TabPane>
      </Tabs>

      {placeSelection && (
        <>
          <p>
            Lugar de asignación:{' '}
            <b>
              {[
                placeSelection.pais_texto,
                placeSelection.estado_texto,
                placeSelection.ciudad_texto,
              ].join(' - ')}
            </b>
          </p>{' '}
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <CustomButton
              type="primary"
              text="Asignar Lugar de ejecución"
              loading={loadingAssign}
              onClick={handleAssign}
            />
          </div>
          <br />
        </>
      )}

      <CustomButton
        type={'default'}
        className={'back-button'}
        htmlType="button"
        text={
          <Link
            to={`${INTERNAL_LINKS.TERM}/${term_id}/${project_id}/${contract_id}/?module=G`}
          >
            Volver
          </Link>
        }
      />
    </>
  );
};

export default TermPlace;
